import React, { useEffect, useState } from 'react'
import { useMediaPredicate } from 'react-media-hook'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Header } from '../../../lib/header'
import ChargePointExpandOptions from '../../../lib/chargePointExpandOptions'
import { ChargingStatistics } from '../../../lib/charging-statistics'
import { LatestChargingSessionsUser } from '../../../lib/latest-charging-sessions-user'
import {
    ChargePoint,
    useStatisticsGetUserUsageStatisticsQuery,
    useChargingSessionGetChargingSessionsForCurrentUserQuery,
    useLazyUserGetOwnedChargePointsWithIdsQuery,
    useUserUpdateUserGreenChargingSettingMutation,
} from '../../../common/redux/api/web-api'
import { CHARGE_POINT_REFRESH_TIMEOUT } from '../../../common/constants/ChargePointConstants'
import { ChargePointOcppAuraControlWidget } from '../../../lib/charge-point-ocpp-aura-control-widget'
import { ChargePointCapiAuraControlWidget } from '../../../lib/charge-point-capi-aura-control-widget'
import { ChargePointCapiHaloControlWidget } from '../../../lib/charge-point-capi-halo-control-widget'
import { ChargePointOcppHaloControlWidget } from '../../../lib/charge-point-ocpp-halo-control-widget'
import { ChargePointDawnControlWidget } from '../../../lib/charge-point-dawn-control-widget'
import { WidgetLoadingState } from '../../../lib/widget-loading-state'
import { RootState } from '../../redux/store'
import useInterval from '../../../common/hooks/useInterval'
import storage from '../../../common/utils/storage'
import InlineAlert, { AlertType } from '../../../common/components/InlineAlert'
import { ChargePointLunaControlWidget } from '../../../lib/charge-point-luna-control-widget'
import useText from '../../../common/hooks/useText'
import { ChargePointStatusWidget } from '../../../common/components/charge-point-status-widget'
import isAura from '../../../lib/isAura'
import isHalo from '../../../lib/isHalo'
import isDawn from '../../../lib/isDawn'
import isLuna from '../../../lib/isLuna'

import { GreenChargingDialog } from './dialogs/green-charging-dialog'

export function UserAppDashboardPage(): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const user = useSelector((state: RootState) => state.user)

    /*************/
    /** States  **/
    /*************/
    const [showGreenChargingDialog, setShowGreenChargingDialog] = useState(false)
    const [chargePointIds, setChargePointIds] = useState<string[]>([])
    const [auraChargePoints, setAuraChargePoints] = useState<ChargePoint[]>([])
    const [haloChargePoints, setHaloChargePoints] = useState<ChargePoint[]>([])
    const [dawnChargePoints, setDawnChargePoints] = useState<ChargePoint[]>([])
    const [lunaChargePoints, setLunaChargePoints] = useState<ChargePoint[]>([])
    const [allReadOnly, setAllReadOnly] = useState(false)

    /*************/
    /** Request **/
    /*************/

    /** Get user statistics **/
    const { data: userStatistics } = useStatisticsGetUserUsageStatisticsQuery({ userId: user.id }, { skip: !user.id })

    /*************************/

    /** Get charging sessions **/
    const { data: chargingSessions, isLoading: chargingSessionLoading } =
        useChargingSessionGetChargingSessionsForCurrentUserQuery({ noOfItems: 7 }, { skip: !user.id })

    /**************************/

    const [getChargePoints, { data: chargePoints, isSuccess, isLoading }] =
        useLazyUserGetOwnedChargePointsWithIdsQuery()

    const [updateUserGreenChargingSetting] = useUserUpdateUserGreenChargingSettingMutation()

    useEffect(() => {
        if (isSuccess && chargePoints) {
            const chargePointIds = chargePoints.map((nextChargePoint: ChargePoint) => {
                return nextChargePoint?.id
            })

            setChargePointIds(chargePointIds)

            if (!chargePointIds) {
                navigate('/registerchargepoint')
            } else {
                if (storage.getItem('showGreenChargingModal')) {
                    setShowGreenChargingDialog(true)
                }

                if (chargePoints) {
                    setAllReadOnly(chargePoints.find((cp) => cp.ownerReadOnly === false) ? false : true)
                }

                const auraChargePoints = chargePoints.filter((c) => isAura(c))
                setAuraChargePoints(auraChargePoints)

                const haloChargePoints = chargePoints.filter((c) => isHalo(c))
                setHaloChargePoints(haloChargePoints)

                const dawnChargePoints = chargePoints.filter((c) => isDawn(c))
                setDawnChargePoints(dawnChargePoints)

                const lunaChargePoints = chargePoints.filter((c) => isLuna(c))
                setLunaChargePoints(lunaChargePoints)
            }
        }
    }, [isSuccess, chargePoints, navigate])
    /**************************/

    /** Hide Green Charging Dialog **/
    const setInformed = () => {
        if (user.id) void updateUserGreenChargingSetting({ userId: user.id })
        storage.removeItem('showGreenChargingModal')
    }
    /********************************/
    useInterval(
        async () => {
            await getChargePoints({
                body: chargePointIds,
                expand: `${ChargePointExpandOptions.OcppConfig},${ChargePointExpandOptions.TopChargingLimitation}`,
            })
        },
        CHARGE_POINT_REFRESH_TIMEOUT * 2,
        true
    )

    const isDesktopScreen = useMediaPredicate('(min-width: 900px)')

    return (
        <div className="page-layout-container">
            <div className="row">
                <div className="col-xs-12">
                    <Header code="lblHomePage" size="lg" />
                    <div className="row">
                        <ChargingStatistics stats={userStatistics} direction={isDesktopScreen ? 'row' : 'column'} />
                    </div>
                    {allReadOnly && (
                        <InlineAlert
                            text={text('lblOwnerReadOnlyActivatedMultiple')}
                            alertType={AlertType.Info}
                            data-testid="ownerReadOnlyActivated-msg"
                        />
                    )}
                    <div className="row margin-bottom">
                        <div className="col-xs-12 col-md-5">
                            {haloChargePoints.map((c) => {
                                if (c.ocppServerMode === 'Internal')
                                    return (
                                        <React.Fragment key={`${c.id}-halo-internal`}>
                                            <ChargePointStatusWidget chargePoint={c} nameInTitle={true} />
                                            <ChargePointOcppHaloControlWidget
                                                chargePoint={c}
                                                isReadOnly={c.ownerReadOnly === true}
                                            />
                                        </React.Fragment>
                                    )
                                else
                                    return (
                                        <ChargePointCapiHaloControlWidget
                                            key={`${c.id}-halo-capi`}
                                            chargePoint={c}
                                            isReadOnly={c.ownerReadOnly === true}
                                        />
                                    )
                            })}
                            {auraChargePoints.map((c) => {
                                if (c.ocppServerMode === 'Internal')
                                    return (
                                        <React.Fragment key={`${c.id}-aura-internal`}>
                                            <ChargePointStatusWidget chargePoint={c} nameInTitle={true} />
                                            <ChargePointOcppAuraControlWidget
                                                chargePoint={c}
                                                userId={user?.id}
                                                isReadOnly={c.ownerReadOnly === true}
                                            />
                                        </React.Fragment>
                                    )
                                else
                                    return (
                                        <ChargePointCapiAuraControlWidget
                                            key={`${c.id}-aura-capi`}
                                            chargePoint={c}
                                            userId={user?.id}
                                            isReadOnly={c.ownerReadOnly === true}
                                        />
                                    )
                            })}
                            {dawnChargePoints.map((c) => (
                                <React.Fragment key={`${c.id}-dawn`}>
                                    <ChargePointStatusWidget chargePoint={c} nameInTitle={true} />
                                    <ChargePointDawnControlWidget
                                        chargePoint={c}
                                        isReadOnly={c.ownerReadOnly === true}
                                    />
                                </React.Fragment>
                            ))}
                            {lunaChargePoints.map((c) => (
                                <React.Fragment key={`${c.id}-luna`}>
                                    <ChargePointStatusWidget chargePoint={c} nameInTitle={true} />
                                    <ChargePointLunaControlWidget
                                        chargePoint={c}
                                        isReadOnly={c.ownerReadOnly === true}
                                    />
                                </React.Fragment>
                            ))}
                            {isLoading && <WidgetLoadingState height="285px" />}
                        </div>
                        {chargingSessions && (
                            <div className="col-xs-12 col-md-7" data-testid="Chargingsessionsection">
                                <LatestChargingSessionsUser sessions={chargingSessions} showEndTime={false} />
                            </div>
                        )}
                        {chargingSessionLoading && (
                            <div className="col-xs-12 col-md-7" data-testid="Chargingsessionsection-loading">
                                <WidgetLoadingState height="450px" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <GreenChargingDialog
                visible={showGreenChargingDialog}
                onAccept={setInformed}
                close={() => setShowGreenChargingDialog(false)}
            />
        </div>
    )
}
